import React from "react";
import { Link } from "react-router-dom";
import ProfilePhoto from "../../../components/Atomos/ProfilePhoto";
import { ReactComponent as EmailIcon } from "../../../assets/images&icons/email.svg";
import type { MemberSearchProfessional } from "../../../Services/MemberSearch";
import { specialCountries } from "../../../utils/const";
import { formatPersonPhoneForDisplay } from "../../../utils/formatPhoneForDisplay";
import { getCountryName } from "../../../utils/formatNames";
import {
  buildFirmPath,
  buildProfessionalPath,
  getPersonPhotoSrc,
} from "../../../utils/memberSearchNavigation";
import { resolvePersonDisplayTitle } from "../../../utils/personDisplayTitle";
import MemberSearchKeywordMatchHint from "./MemberSearchKeywordMatchHint";
import "../../../pages/AboutUs/AboutUs.css";

type MemberSearchProfessionalCardProps = {
  person: MemberSearchProfessional;
  keyword?: string;
};

const formatFirmServiceType = (serviceType?: string) => {
  if (!serviceType || serviceType === "Other") {
    return "Legal Services";
  }
  return serviceType;
};

const formatFirmLocation = (country?: string, state?: string) => {
  if (!country) {
    return "";
  }
  if (specialCountries.includes(country) && state) {
    return `${state}, ${getCountryName(country)}`;
  }
  return getCountryName(country);
};

const MemberSearchProfessionalCard: React.FC<MemberSearchProfessionalCardProps> = ({
  person,
  keyword = "",
}) => {
  const photoSrc = getPersonPhotoSrc(person);
  const profilePath = buildProfessionalPath(person, { highlight: keyword });
  const firmPath = person.firmName
    ? buildFirmPath({
        id: person.firmId ?? "",
        name: person.firmName,
        legacyId: person.firmLegacyId,
        shortId: person.firmShortId,
      })
    : null;
  const displayTitle = resolvePersonDisplayTitle(person);
  const phoneDisplay = formatPersonPhoneForDisplay(person);
  const locationLabel = formatFirmLocation(person.firmCountry, person.firmState);

  return (
    <div className="relative flex h-full flex-col hover:z-20">
      <div className="flex items-start gap-4">
        <div className="flex h-[90px] w-[90px] shrink-0 items-center justify-center overflow-hidden rounded-full">
          <ProfilePhoto
            src={photoSrc}
            alt={`${person.name} ${person.lastName}`}
            imgClassName="h-full w-full object-cover object-[center_10%]"
          />
        </div>

        <div className="min-w-0 flex-1">
          <Link
            to={profilePath}
            className="council-name block text-left leading-5 transition-colors hover:text-red-600 hover:underline"
          >
            {person.name} {person.lastName}
          </Link>

          <p className="mb-2 text-left font-roboto text-[17px] italic !font-[400] text-[#474F56]">
            {displayTitle}
          </p>

          <div className="mb-2 flex flex-wrap items-center gap-2">
            <p className="council-services m-0 text-left">
              {phoneDisplay || "No phone number"}
            </p>
            {person.firmEmail ? (
              <>
                <div className="h-4 w-0.5 bg-gray-300" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => window.open(`mailto:${person.firmEmail}`, "_blank")}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-red-600"
                  title={`Email ${person.name} ${person.lastName}`}
                >
                  <EmailIcon />
                </button>
              </>
            ) : null}
          </div>

          <div className="space-y-0">
            {person.firmName ? (
              firmPath ? (
                <Link
                  to={firmPath}
                  className="council-firm block text-left transition-colors hover:text-red-600 hover:underline"
                >
                  {person.firmName}
                </Link>
              ) : (
                <p className="council-firm m-0 text-left">{person.firmName}</p>
              )
            ) : null}

            {person.firmServiceType || person.firmName ? (
              <p className="council-services m-0 text-left">
                {formatFirmServiceType(person.firmServiceType)}
              </p>
            ) : null}

            {locationLabel ? (
              <p className="council-address m-0 text-left italic">{locationLabel}</p>
            ) : null}
          </div>
        </div>
      </div>

      {person.hiddenKeywordMatch && keyword.trim() ? (
        <div className="mt-auto w-full pt-3">
          <MemberSearchKeywordMatchHint
            match={person.hiddenKeywordMatch}
            keyword={keyword}
          />
        </div>
      ) : null}
    </div>
  );
};

export default MemberSearchProfessionalCard;
